// Teeth Drop (docs/04 §17.4, docs/07 §2): eyeballs dropped through a board of teeth. The player aims
// and drops; the sim walks each ball down the pegs and the view only replays the returned path.
import { Container, Graphics, Sprite } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import { getAtlas, textTexture } from '../../art/rasterize';
import { audio } from '../../audio/audio';
import { B } from '../../content/balance';
import { formatNumber } from '../../i18n/format';
import { t } from '../../i18n/t';
import { PLINKO_SLOTS } from '../../sim/bonus/plinko';
import type { PlinkoBonus } from '../../sim/types';
import { dispatch, getGame } from '../../state/store';
import { LOGICAL_W } from '../app';
import type { Fx } from '../fx/fx';
import type { Popups } from '../fx/Popups';
import { PixelButton } from '../machine/PixelButton';
import { clock } from '../timeline/clock';

const W = 300;
const H = 216;
/** Half-bucket unit in pixels, board origin and row pitch. */
const U = 14;
const X0 = 24;
const Y0 = 34;
const ROW = 8;
const BUCKET_Y = Y0 + B.PLINKO_ROWS * ROW + 4;
const px = (pos: number) => X0 + pos * U;

const plinko = (): PlinkoBonus | null => {
  const b = getGame().run?.bonus;
  return b?.mode === 'plinko' ? b : null;
};

export class PlinkoView extends Container {
  private panel = new Container();
  private board = new Container();
  private labels = new Container();
  private aim = new Sprite();
  private ball = new Sprite();
  private balls = new Sprite();
  private won = new Sprite();
  private left = new PixelButton(20, 15, '<');
  private right = new PixelButton(20, 15, '>');
  private drop = new PixelButton(60, 15, t('ui.plinko.drop'));
  private slot = Math.floor(PLINKO_SLOTS / 2);
  private seen = 0;
  private busy = false;

  constructor(
    private popups: Popups,
    private fx: Fx,
  ) {
    super();
    this.visible = false;
    this.addChild(this.panel);
    const bg = new Graphics();
    bg.rect(-2, -2, W + 4, H + 4).fill(PALETTE.ink);
    bg.rect(0, 0, W, H).fill(PALETTE.maroon);
    bg.rect(0, 0, W, 14).fill(PALETTE.blood);
    bg.rect(4, 18, W - 8, H - 44).fill(PALETTE.night);
    const title = new Sprite(textTexture(t('ui.plinko.title'), 'white', 'crimson'));
    title.position.set(Math.round((W - title.width) / 2), 3);
    const a = getAtlas();
    this.aim.texture = a.tex('ball_eye');
    this.aim.anchor.set(0.5);
    this.aim.alpha = 0.6;
    this.ball.texture = a.tex('ball_eye');
    this.ball.anchor.set(0.5);
    this.ball.visible = false;
    this.balls.position.set(8, H - 22);
    this.won.position.set(8, H - 12);
    this.left.position.set(150, H - 22);
    this.right.position.set(172, H - 22);
    this.drop.position.set(W - 68, H - 22);
    this.left.onClick = () => this.move(-1);
    this.right.onClick = () => this.move(1);
    this.drop.onClick = () => this.dropNow();
    // Clicking the board drops straight from that column.
    const hit = new Graphics();
    hit.rect(X0 - U, 18, (PLINKO_SLOTS * 2 + 2) * U, BUCKET_Y - 18).fill({ color: PALETTE.ink, alpha: 0.001 });
    hit.eventMode = 'static';
    hit.cursor = 'pointer';
    hit.on('pointermove', (e) => this.aimAt(this.panel.toLocal(e.global).x));
    hit.on('pointertap', (e) => {
      this.aimAt(this.panel.toLocal(e.global).x);
      this.dropNow();
    });
    this.panel.addChild(bg, title, this.board, this.labels, hit, this.aim, this.ball, this.balls, this.won, this.left, this.right, this.drop);
  }

  private aimAt(x: number): void {
    this.slot = Math.max(0, Math.min(PLINKO_SLOTS - 1, Math.floor((x - X0) / (U * 2))));
    this.placeAim();
  }

  move(dir: -1 | 1): void {
    this.slot = Math.max(0, Math.min(PLINKO_SLOTS - 1, this.slot + dir));
    this.placeAim();
  }

  dropNow(): void {
    const b = plinko();
    if (!b || b.done || this.busy) return;
    audio.play('click');
    dispatch({ t: 'BONUS_INPUT', input: { kind: 'plinko_drop', slot: this.slot } });
  }

  private placeAim(): void {
    this.aim.position.set(px(this.slot * 2 + 1), 24);
  }

  private drawBoard(b: PlinkoBonus): void {
    this.board.removeChildren().forEach((c) => c.destroy());
    this.labels.removeChildren().forEach((c) => c.destroy());
    const a = getAtlas();
    const golden = new Set(b.golden.map(([r, p]) => `${r + 1}:${p}`));
    // The ball arrives at row r on a position with parity (r + 1) % 2 and bounces off the tooth there.
    for (let r = 0; r < B.PLINKO_ROWS; r++)
      for (let p = 1; p < PLINKO_SLOTS * 2; p++) {
        if (p % 2 !== (r + 1) % 2) continue;
        const peg = new Sprite(a.tex(golden.has(`${r}:${p}`) ? 'peg_gold' : 'peg_tooth'));
        peg.anchor.set(0.5);
        peg.position.set(px(p), Y0 + r * ROW - 3);
        this.board.addChild(peg);
      }
    const g = new Graphics();
    for (let i = 0; i < PLINKO_SLOTS; i++) {
      const x = px(i * 2);
      const prize = b.plugBucket === i ? 'plug' : B.PLINKO_BUCKETS[i]!;
      const chomp = prize === 'chomp';
      g.rect(x + 1, BUCKET_Y, U * 2 - 2, 18).fill(chomp ? PALETTE.blood : prize === 'plug' ? PALETTE.indigo : Number(prize) >= 50 ? PALETTE.gold : PALETTE.charcoal);
      g.rect(x + 1, BUCKET_Y, U * 2 - 2, 18).stroke({ color: PALETTE.ink, width: 1 });
      const label = new Sprite(
        textTexture(chomp ? t('ui.plinko.chompShort') : prize === 'plug' ? t('ui.plinko.plug') : `×${prize}`, chomp ? 'white' : Number(prize) >= 50 ? 'ink' : 'paper', null),
      );
      if (label.width > U * 2 - 2) label.scale.set(0.5);
      label.position.set(Math.round(x + U - label.width / 2), BUCKET_Y + 6);
      this.labels.addChild(label);
    }
    this.board.addChild(g);
  }

  private refresh(b: PlinkoBonus): void {
    this.balls.texture = textTexture(t('ui.plinko.balls', { n: b.balls }), 'paper', 'ink');
    this.won.texture = textTexture(t('ui.plinko.won', { n: formatNumber(b.won, getGame().meta.settings.notation) }), 'gold', 'ink');
    const live = !b.done && !this.busy;
    this.left.set(live);
    this.right.set(live);
    this.drop.set(live);
    this.aim.visible = live;
  }

  open(): void {
    const b = plinko();
    if (!b) return;
    this.seen = b.drops.length;
    this.busy = false;
    this.drawBoard(b);
    this.placeAim();
    this.refresh(b);
    this.panel.position.set(Math.round(LOGICAL_W / 2 - W / 2 - 20), 370);
    this.visible = true;
    void clock.tween(this.panel, 350, { y: 66, ease: 'back.out(1.4)' });
  }

  /** New drops: replay each ball's path peg by peg, then light its bucket. */
  async update(): Promise<void> {
    const b = plinko();
    if (!b) return;
    this.busy = true;
    this.refresh(b);
    const fresh = b.drops.slice(this.seen);
    this.seen = b.drops.length;
    const quick = fresh.length > 2;
    for (const d of fresh) {
      this.ball.visible = true;
      this.ball.position.set(px(d.start * 2 + 1), 24);
      const step = quick ? 18 : 45;
      for (let r = 0; r < d.path.length; r++) {
        await clock.tween(this.ball, step, { x: px(d.path[r]!), y: Y0 + r * ROW + 2, ease: 'sine.in' });
        if (!quick && r % 3 === 0) audio.play('wheelTick', { pitch: r - 8, volume: 0.4 });
      }
      await clock.tween(this.ball, step * 2, { y: BUCKET_Y + 9, ease: 'bounce.out' });
      const at = { x: this.panel.x + this.ball.x, y: this.panel.y + BUCKET_Y - 6 };
      if (d.prize === 'chomp') {
        audio.play('chomp');
        this.fx.shake(3, 250);
        void this.popups.float(t('ui.plinko.chomp'), at.x, at.y, 'red', 1, 700);
      } else if (d.prize === 'plug') {
        audio.play('choir');
        void this.popups.float(t('ui.plinko.plug'), at.x, at.y, 'lavender', 2, 900);
      } else {
        audio.play('patternBlip', { pitch: Math.min(12, Number(d.prize) / 5) });
        void this.popups.float(`×${Number(d.prize) * (d.golden ? b.goldenX : 1)}`, at.x, at.y, d.golden ? 'gold' : 'paper', d.golden ? 2 : 1, 700);
      }
      this.ball.visible = false;
    }
    this.busy = false;
    this.refresh(b);
  }

  async close(payout: string, voided: boolean): Promise<void> {
    const mid = { x: this.panel.x + W / 2, y: this.panel.y + H / 2 };
    if (!voided) {
      audio.play('scratchWin');
      await this.popups.banner(t('ui.scratch.win', { coins: formatNumber(payout) }), mid.x, mid.y, 'gold', 2, 700);
    }
    await clock.tween(this.panel, 300, { y: 390, ease: 'back.in(1.4)' });
    this.visible = false;
  }
}
